import { useState } from 'react';
import { ArrowRightIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/language-context';
import { formatDate } from '@/routes/jobs/helpers';

interface Props {
  /** What's being moved — a job title or visit label. Shown as a subtitle. */
  title: string;
  /** Current scheduled date, `YYYY-MM-DD`. */
  currentDate: string;
  /**
   * The proposed new date, `YYYY-MM-DD`. When set the dialog is a plain
   * confirm (current → new); when omitted it shows a date picker seeded from
   * `currentDate`.
   */
  targetDate?: string;
  isPending: boolean;
  onConfirm: (newISO: string) => void;
  onCancel: () => void;
}

export function RescheduleDialog({
  title,
  currentDate,
  targetDate,
  isPending,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useLanguage();
  const [draft, setDraft] = useState(currentDate);

  const nextDate = targetDate ?? draft;
  const unchanged = !nextDate || nextDate === currentDate;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="reschedule-dialog"
    >
      <div
        className="flex w-full max-w-sm flex-col rounded-xl border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{t.reschedule}</h2>
            <p className="truncate text-xs text-muted-foreground">{title}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
            data-testid="reschedule-cancel-x"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-3 px-5 py-4 text-sm">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-muted-foreground">
              {t.rescheduleCurrentLabel}
            </span>
            {targetDate ? (
              <span className="flex items-center gap-2">
                <span>{formatDate(currentDate)}</span>
                <ArrowRightIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-semibold text-primary">
                  {formatDate(targetDate)}
                </span>
              </span>
            ) : (
              <span className="font-medium">{formatDate(currentDate)}</span>
            )}
          </div>

          {!targetDate && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reschedule-date">{t.rescheduleNewLabel}</Label>
              <Input
                id="reschedule-date"
                type="date"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="reschedule-date-input"
                className="w-fit"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-4">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onCancel}
            data-testid="reschedule-cancel"
          >
            {t.cancel}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={isPending || unchanged}
            onClick={() => onConfirm(nextDate)}
            data-testid="reschedule-confirm"
          >
            {t.reschedule}
          </Button>
        </div>
      </div>
    </div>
  );
}
